import type { Finding } from './api.ts'

/**
 * What a registration row says about its parts (#612).
 *
 * Scoring compares council, jurisdiction, number and qualifier apart and
 * records which diverged and which could not be verified. This is that, in
 * the pharmacist's words. Never narrated by the explainer (`fixedWords`), so
 * this is the row's only sentence.
 */

/** A mirror of `RegistrationPart` in `@rp/core`, which the web cannot import. */
export enum RegistrationPart {
  Council = 'council',
  Jurisdiction = 'jurisdiction',
  Number = 'number',
  Qualifier = 'qualifier',
}

const PART_LABEL: Readonly<Record<RegistrationPart, string>> = {
  [RegistrationPart.Council]: 'o conselho',
  [RegistrationPart.Jurisdiction]: 'a UF/região',
  [RegistrationPart.Number]: 'o número',
  [RegistrationPart.Qualifier]: 'a categoria',
}

function partsOf(value: unknown): readonly RegistrationPart[] {
  if (!Array.isArray(value)) return []
  const known = Object.values<string>(RegistrationPart)
  return value.filter((one): one is RegistrationPart => known.includes(one as string))
}

function listed(parts: readonly RegistrationPart[]): string {
  const labels = parts.map((part) => PART_LABEL[part])
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`
}

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * The sentence, or undefined for a finding that predates #612 or has nothing
 * to add -- a full match, or an absent side, which `undecidedReason` says.
 */
export function registrationReason(finding: Finding): string | undefined {
  const evidence = (finding.evidence ?? {}) as Record<string, unknown>
  const diverged = partsOf(evidence.diverged)
  const unverified = partsOf(evidence.unverified)
  const sentences: string[] = []
  if (diverged.length > 0) {
    sentences.push(
      `${capitalised(listed(diverged))} ${diverged.length > 1 ? 'divergem' : 'diverge'}.`,
    )
  }
  const numbersApart = evidence.reason === 'the numbers could not be compared'
  if (numbersApart) {
    sentences.push('Os números não puderam ser comparados: confira o registro na receita.')
  }
  const rest = numbersApart
    ? unverified.filter((part) => part !== RegistrationPart.Number)
    : unverified
  if (rest.length > 0) {
    sentences.push(
      `${capitalised(listed(rest))} ${rest.length > 1 ? 'ficaram' : 'ficou'} sem verificação.`,
    )
  }
  return sentences.length === 0 ? undefined : sentences.join(' ')
}
